import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Space, Button, Modal, InputNumber, Select, Input, DatePicker, message, Typography, Tag, Row, Col, Statistic } from 'antd';
import { PlusOutlined, ReloadOutlined, WalletOutlined, ClockCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import api from '../../../services/api';
import dayjs from 'dayjs';

const { Text } = Typography;
const fmtBRL = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

export default function AccountEntriesTable({ communityTeamId, onBalanceChange }) {
  const [data, setData] = useState({ items: [], total: 0, balance_brl: 0, opened_at: null });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ from: null, to: null, direction: null });
  const [pagination, setPagination] = useState({ page: 1, limit: 50 });
  const [entryModal, setEntryModal] = useState({ open: false, direction: 'in', amount: 0, category: 'manual_adjustment', description: '' });

  const load = useCallback(async () => {
    if (!communityTeamId) return;
    setLoading(true);
    try {
      const params = { team_id: communityTeamId, page: pagination.page, limit: pagination.limit };
      if (filters.from) params.from = filters.from.format('YYYY-MM-DD');
      if (filters.to) params.to = filters.to.format('YYYY-MM-DD');
      if (filters.direction) params.direction = filters.direction;
      const r = await api.get('/community/finance/accounts/entries', { params });
      setData(r.data);
      onBalanceChange?.(r.data.balance_brl);
    } catch (e) {
      message.error(e.response?.data?.message || 'Falhou carregar extrato');
    } finally { setLoading(false); }
  }, [communityTeamId, filters, pagination.page, pagination.limit, onBalanceChange]);
  useEffect(() => { load(); }, [load]);

  // Exporta CSV do período filtrado. Baixa TODAS as entradas do filtro
  // (não só a página atual) chamando o endpoint com limit alto.
  const exportCsv = async () => {
    try {
      const params = { team_id: communityTeamId, page: 1, limit: 5000 };
      if (filters.from) params.from = filters.from.format('YYYY-MM-DD');
      if (filters.to) params.to = filters.to.format('YYYY-MM-DD');
      if (filters.direction) params.direction = filters.direction;
      const r = await api.get('/community/finance/accounts/entries', { params });
      const items = r.data.items || [];
      if (items.length === 0) return message.warning('Sem lançamentos no período');
      const esc = (v) => {
        const s = v == null ? '' : String(v);
        return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const header = ['Data', 'Categoria', 'Direção', 'Valor (R$)', 'Descrição', 'Pagador', 'Método', 'Libera em'];
      const rows = items.map(x => [
        dayjs(x.created_at).format('DD/MM/YYYY HH:mm'),
        catCfg(x.category).label,
        x.direction === 'in' ? 'Entrada' : 'Saída',
        Number(x.amount_brl || 0).toFixed(2).replace('.', ','),
        x.description || '',
        x.payer_name || '',
        methodLabelOf(x.paid_method) || '',
        x.expected_release_at ? dayjs(x.expected_release_at).format('DD/MM/YYYY') : '',
      ].map(esc).join(';'));
      // BOM UTF-8 pra Excel abrir com acentos ok. Usa ';' (padrão pt-BR).
      const csv = '﻿' + header.join(';') + '\n' + rows.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const from = filters.from ? filters.from.format('YYYY-MM-DD') : 'inicio';
      const to = filters.to ? filters.to.format('YYYY-MM-DD') : 'hoje';
      a.href = url;
      a.download = `minha-conta_${from}_ate_${to}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      message.success(`${items.length} lançamentos exportados`);
    } catch (e) {
      message.error(e.response?.data?.message || 'Falhou exportar');
    }
  };

  const saveEntry = async () => {
    if (!entryModal.amount || entryModal.amount <= 0) return message.warning('Informe o valor');
    try {
      await api.post('/community/finance/accounts/entries', {
        community_team_id: communityTeamId,
        direction: entryModal.direction,
        category: entryModal.category,
        amount_brl: Number(entryModal.amount),
        description: entryModal.description || null,
      });
      message.success('Lançado');
      setEntryModal({ open: false, direction: 'in', amount: 0, category: 'manual_adjustment', description: '' });
      load();
    } catch (e) {
      message.error(e.response?.data?.message || 'Falhou lançar');
    }
  };

  const CATEGORY_MAP = {
    charge_paid: { label: 'Cobrança paga', color: 'green' },
    refund: { label: 'Estorno', color: 'red' },
    credit_purchase: { label: 'Compra de crédito', color: 'blue' },
    manual_adjustment: { label: 'Ajuste manual', color: 'default' },
  };
  const catCfg = v => CATEGORY_MAP[v] || { label: v, color: 'default' };
  const methodLabelOf = m => m === 'credit_card' ? 'Cartão' : (m === 'pix' ? 'Pix' : null);
  const renderDesc = (v, r) => {
    const methodLabel = methodLabelOf(r.paid_method);
    const releaseDate = r.expected_release_at ? dayjs(r.expected_release_at) : null;
    const isFutureRelease = releaseDate && releaseDate.isAfter(dayjs());
    // Só mostra info de liberação pra entries que são pagamento (direction='in'
    // + tem paid_method — ou seja, veio de uma cobrança).
    const showRelease = r.direction === 'in' && r.paid_method && releaseDate;
    return (
      <>
        <div>{v || <Text type="secondary">—</Text>}</div>
        {r.payer_name && (
          <div style={{ marginTop: 2 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Pagador: {r.payer_name}</Text>
            {methodLabel && (
              <Tag color={methodLabel === 'Pix' ? 'green' : 'blue'} style={{ marginLeft: 6, fontSize: 10 }}>{methodLabel}</Tag>
            )}
          </div>
        )}
        {showRelease && (
          <div>
            {isFutureRelease ? (
              <Text type="warning" style={{ fontSize: 11 }}>
                💰 Cai na sua conta MP em {releaseDate.format('DD/MM/YYYY')}
              </Text>
            ) : (
              <Text type="success" style={{ fontSize: 11 }}>
                ✓ Valor já disponível na sua conta MP
              </Text>
            )}
          </div>
        )}
        {r.gateway_payment_id && (
          <div><Text type="secondary" style={{ fontSize: 10 }}>MP #{r.gateway_payment_id}</Text></div>
        )}
      </>
    );
  };

  const columns = [
    { title: 'Data', dataIndex: 'created_at', width: 90,
      render: v => v ? <span style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{dayjs(v).format('DD/MM HH:mm')}</span> : '—' },
    { title: 'Descrição', dataIndex: 'description', render: renderDesc },
    { title: 'Categoria', dataIndex: 'category', width: 140,
      render: v => { const c = catCfg(v); return <Tag color={c.color}>{c.label}</Tag>; } },
    { title: 'Valor', dataIndex: 'amount_brl', width: 110, align: 'right',
      render: (v, r) => (
        <Text strong style={{ color: r.direction === 'in' ? '#128C7E' : '#dc2626', whiteSpace: 'nowrap' }}>
          {r.direction === 'in' ? '+' : '−'} {fmtBRL(v)}
        </Text>
      ) },
  ];

  return (
    <>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12}>
          <Card size="small" styles={{ body: { padding: 16 } }}
            style={{ borderColor: '#128C7E', background: '#f0faf7' }}>
            <Statistic
              title={<Space><WalletOutlined /><Text strong>Saldo disponível</Text></Space>}
              value={data.balance_brl || 0}
              precision={2}
              prefix="R$"
              valueStyle={{ color: '#128C7E', fontSize: 28 }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>Já liberado pra saque no MP</Text>
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card size="small" styles={{ body: { padding: 16 } }}
            style={{ borderColor: '#faad14', background: '#fffbe6' }}>
            <Statistic
              title={<Space><ClockCircleOutlined /><Text strong>A receber</Text></Space>}
              value={data.pending_release_brl || 0}
              precision={2}
              prefix="R$"
              valueStyle={{ color: '#faad14', fontSize: 28 }}
              suffix={data.pending_release_count > 0 ? <Text type="secondary" style={{ fontSize: 12 }}> ({data.pending_release_count} cobr.)</Text> : null}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>Cartão em processamento MP (~14 dias)</Text>
          </Card>
        </Col>
      </Row>

    <Card
      title="Extrato"
      styles={{ header: { padding: '12px 16px' }, body: { padding: 12 } }}
      extra={
        <Space wrap size={[6, 6]}>
          <DatePicker placeholder="De" value={filters.from} onChange={v => setFilters(f => ({ ...f, from: v }))} style={{ width: 130 }} />
          <DatePicker placeholder="Até" value={filters.to} onChange={v => setFilters(f => ({ ...f, to: v }))} style={{ width: 130 }} />
          <Select allowClear placeholder="Direção" style={{ width: 130 }}
            value={filters.direction} onChange={v => setFilters(f => ({ ...f, direction: v }))}
            options={[{value: 'in', label: 'Entradas'}, {value: 'out', label: 'Saídas'}]} />
          <Button icon={<ReloadOutlined />} onClick={load} />
          <Button icon={<DownloadOutlined />} onClick={exportCsv}>Exportar CSV</Button>
          <Button type="primary" icon={<PlusOutlined />}
            style={{ background: '#128C7E', borderColor: '#128C7E' }}
            onClick={() => setEntryModal(s => ({ ...s, open: true }))}>
            Lançar entrada/saída
          </Button>
        </Space>
      }
    >
      <div style={{ overflowX: 'auto' }}>
        <Table rowKey="id" size="small" loading={loading}
          columns={columns} dataSource={data.items}
          scroll={{ x: 620 }}
          pagination={{ total: data.total, current: pagination.page, pageSize: pagination.limit,
            simple: true, size: 'small',
            onChange: (page, limit) => setPagination({ page, limit }) }} />
      </div>

      <Modal title={entryModal.direction === 'in' ? 'Registrar entrada' : 'Registrar saída'}
        open={entryModal.open}
        onCancel={() => setEntryModal(s => ({ ...s, open: false }))}
        onOk={saveEntry} okText="Registrar" destroyOnClose>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Select style={{ width: '100%' }} value={entryModal.direction}
            onChange={v => setEntryModal(s => ({ ...s, direction: v }))}
            options={[{value: 'in', label: 'Entrada'}, {value: 'out', label: 'Saída'}]} />
          <Select style={{ width: '100%' }} value={entryModal.category}
            onChange={v => setEntryModal(s => ({ ...s, category: v }))}
            options={[
              {value: 'manual_adjustment', label: 'Ajuste manual'},
              {value: 'withdrawal', label: 'Retirada'},
              {value: 'other', label: 'Outro'},
            ]} />
          <InputNumber style={{ width: '100%' }} min={0} step={1} precision={2}
            value={entryModal.amount} onChange={v => setEntryModal(s => ({ ...s, amount: v }))}
            prefix="R$" placeholder="0,00" />
          <Input.TextArea rows={2} maxLength={500}
            value={entryModal.description} onChange={e => setEntryModal(s => ({ ...s, description: e.target.value }))}
            placeholder="Descrição (opcional)" />
        </Space>
      </Modal>
    </Card>
    </>
  );
}
